import React from 'react';

interface KoMoldLogoProps {
    size?: number;
    className?: string;
    animate?: boolean;
    variant?: 'default' | 'splash' | 'loading';
    transparent?: boolean;
}

const KoMoldLogo: React.FC<KoMoldLogoProps> = ({
    size = 32,
    className = '',
    animate = false,
    variant = 'default',
    transparent = false
}) => {
    const getAnimationClass = () => {
        if (!animate) return '';

        switch (variant) {
            case 'splash':
                return 'animate-splash-in';
            case 'loading':
                return 'animate-spin-slow';
            default:
                return 'animate-hover-float';
        }
    };

    const isSplash = variant === 'splash';
    const containerSize = isSplash ? 'w-20 h-20 sm:w-24 sm:h-24' : '';

    // Calculate SVG size based on size prop
    const getSvgSize = () => {
        if (isSplash) return 'w-14 h-14 sm:w-16 sm:h-16';
        const svgSize = Math.max(size * 0.625, 16); // 62.5% of container, min 16px
        return `w-[${svgSize}px] h-[${svgSize}px]`;
    };

    // Calculate container size from prop if not splash
    const getContainerSize = () => {
        if (isSplash) return containerSize;
        return `style="width: ${size}px; height: ${size}px;"`;
    };

    // Container styles
    const containerClassName = `
        ${transparent ? '' : 'bg-white'} rounded-lg flex items-center justify-center
        ${transparent ? '' : (isSplash ? 'shadow-xl' : 'shadow-md')}
        ${transparent ? '' : 'border border-gray-200'}
        ${getAnimationClass()}
        ${className}
    `;

    return (
        <div
            className={containerClassName}
            {...(isSplash ? {} : { style: { width: size, height: size } })}
        >
            <svg
                viewBox="0 0 24 24"
                className={isSplash ? 'w-14 h-14 sm:w-16 sm:h-16' : ''}
                style={isSplash ? {} : { width: size * 0.625, height: size * 0.625 }}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                {/* Top face - lighter */}
                <path
                    d="M12 3L20 7.5L12 12L4 7.5L12 3Z"
                    fill="#60A5FA"
                    fillOpacity="0.9"
                    className={animate && variant === 'loading' ? 'animate-pulse' : ''}
                />
                {/* Left face - medium */}
                <path
                    d="M4 7.5L12 12V21L4 16.5V7.5Z"
                    fill="#3B82F6"
                    fillOpacity="0.8"
                    className={animate && variant === 'loading' ? 'animate-pulse' : ''}
                    style={{ animationDelay: '0.1s' }}
                />
                {/* Right face - darker */}
                <path
                    d="M20 7.5V16.5L12 21V12L20 7.5Z"
                    fill="#2563EB"
                    fillOpacity="0.7"
                    className={animate && variant === 'loading' ? 'animate-pulse' : ''}
                    style={{ animationDelay: '0.2s' }}
                />
                {/* Cube edges */}
                <path
                    d="M12 3L12 12M12 12V21M12 12L20 7.5M12 12L4 7.5M20 7.5V16.5M4 7.5V16.5"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Mold cavity highlight */}
                <path
                    d="M12 8L15 9.5V12.5L12 14L9 12.5V9.5L12 8Z"
                    fill="none"
                    stroke="#93C5FD"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    className={animate && variant === 'splash' ? 'animate-pulse' : ''}
                />
                {/* Inner cavity detail */}
                <circle
                    cx="12"
                    cy="11"
                    r="1.5"
                    fill="#FCD34D"
                    fillOpacity="0.8"
                    className={animate && variant === 'splash' ? 'animate-ping' : ''}
                />
            </svg>
        </div>
    );
};

export default KoMoldLogo;
